import React, { Fragment, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Autocomplete,
    Box,
    FormControl,
    MenuItem,
    TextField,
    Tooltip,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import {
    getGoogleEnhanceEvents,
    getGoogleEnhanceMapping,
    getGoogleWebMapping,
} from "../helpers/destinations/createDestination";
import StandartButton from "./atoms/StandartButton";
import SwalAlertComp from "./SwalAlert";

const GoogleOfflinePixels = ({
    destinationType,
    setEnhanceAccounts,
    setEnhanceMapping,
    editMappingStatu,
    googleEnhanceData,
    ggadsDestinationId,
    setButtonDisabledStatus,
}) => {
    const language = useSelector((state) => state.languagesState.languages);

    // destinationType can be 'ggads' (offline) or 'ggads_web' (web conversions)
    const isWebDestination = destinationType === "ggads_web";

    const [fieldList, setFieldList] = useState([]);
    const [eventList, setEventList] = useState([]);
    const [adAccountIdList, setAdAccountIdList] = useState([]);
    const [selectedAdAccountId, setSelectedAdAccountId] = useState();
    const [accountTagsTarget, setAccountTagsTarget] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const filteredAccountTagsTarget =
        accountTagsTarget?.filter((target) => {
            const hasAwTag = target?.tag?.startsWith("AW-");
            return isWebDestination ? hasAwTag : !hasAwTag;
        }) || [];

    const handleAdAccountIdChange = ({ target: { value } }) => {
        if (String(value) !== String(selectedAdAccountId)) {
            setFieldList([
                {
                    event: "",
                    target: "",
                    targetError: false,
                    customTarget: "",
                    customTargetError: false,
                },
            ]);
            setAccountTagsTarget([]);
        }
        setSelectedAdAccountId(value);
        if (setEnhanceAccounts !== undefined) {
            setEnhanceAccounts(value);
        }
    };

    const handleEventChange = ({ target: { value } }, index, isCustom) => {
        let fieldArr = [...fieldList];
        fieldArr[index] = {
            ...fieldArr[index],
            ...(isCustom
                ? { customEvent: value, customEventError: !value }
                : {
                      event: value?.api_value || value,
                      eventError: !(value?.api_value || value),
                  }),
        };
        setFieldList(fieldArr);
    };
    // {accountTagsTarget?.map((option) => option?.tag

    const handleTargetChange = ({ target: { value } }, index, isCustom) => {
        let newArr = [...fieldList];
        let regex = /AW-\d+\/[A-Za-z0-9_]+/;

        if (!isCustom && !value) {
            newArr[index] = {
                ...newArr[index],
                target: "",
                targetId: undefined,
                targetName: undefined,
                targetError: true,
                noTag: true,
            };
            setFieldList(newArr);
            return;
        }

        const isId = parseInt(value) ? true : false;
        const target = filteredAccountTagsTarget?.find(
            (account) =>
                account?.tag === value?.tag || account?.id === value?.id,
        );
        newArr[index] = {
            event: newArr[index].event,
            ...newArr[index],
            ...(isCustom
                ? {
                      customTarget: value,
                      customTargetError: isWebDestination
                          ? !regex.test(value)
                          : !value,
                  }
                : {
                      target: value?.tag || value?.id,
                      targetId: isId ? parseInt(value) : target?.id,
                      targetName: target?.name,
                      noTag: true,
                  }),
            ...(value === "custom"
                ? {
                      target: "custom",
                      targetError: false,
                      customTarget: "",
                      customTargetError: false,
                  }
                : {}),
        };
        setFieldList(newArr);
    };
    const removeField = (index) => {
        let fieldArr = [...fieldList];
        fieldArr.splice(index, 1);
        setFieldList(fieldArr);
    };

    const addField = () => {
        setFieldList([
            ...fieldList,
            {
                event: "",
                target: "",
                targetError: false,
                customTarget: "",
                customTargetError: false,
            },
        ]);
    };

    useEffect(() => {
        const _getGoogleEnhanceEvents = async () => {
            setIsLoading(true);
            let response = await getGoogleEnhanceEvents();
            if (response.status) {
                let events = response?.data?.map((x) => ({
                    api_value: x,
                    display_value: x,
                }));
                setEventList(events);
                if (editMappingStatu === undefined) {
                    setFieldList([
                        ...fieldList,
                        {
                            event: "",
                            target: "",
                            targetError: false,
                            customTarget: "",
                            customTargetError: false,
                        },
                    ]);
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
            setIsLoading(false);
        };

        _getGoogleEnhanceEvents();

        if (googleEnhanceData !== undefined && googleEnhanceData.length > 0) {
            let googleEnhanceDataMapped = googleEnhanceData.map((x) => ({
                api_value: x?.account_id,
                display_value: x?.name,
            }));
            setEnhanceAccounts(googleEnhanceDataMapped[0].api_value);
            setAdAccountIdList(googleEnhanceDataMapped);
            setSelectedAdAccountId(googleEnhanceDataMapped[0]?.api_value);

            setAccountTagsTarget(googleEnhanceDataMapped[0]?.tags);
        }
    }, []);

    useEffect(() => {
        if (selectedAdAccountId && googleEnhanceData)
            setAccountTagsTarget(
                googleEnhanceData?.find(
                    (account) => account?.account_id == selectedAdAccountId,
                )?.tags,
            );
    }, [selectedAdAccountId, googleEnhanceData]);

    // Apply refreshed accounts/tags from parent (e.g. after refresh-tags API).
    // Only depend on googleEnhanceData so we don't re-run when user changes account dropdown.
    useEffect(() => {
        if (
            !googleEnhanceData ||
            !Array.isArray(googleEnhanceData) ||
            googleEnhanceData.length === 0
        )
            return;
        const mapped = googleEnhanceData.map((x) => ({
            api_value: x?.account_id,
            display_value: x?.name ?? x?.account_id,
        }));
        setAdAccountIdList(mapped);
        const firstAccountId = mapped[0]?.api_value;
        const keepSelection = mapped.some(
            (m) => String(m.api_value) === String(selectedAdAccountId),
        );
        const selectedId = keepSelection ? selectedAdAccountId : firstAccountId;
        setSelectedAdAccountId(selectedId);
        const account = googleEnhanceData.find(
            (a) => String(a?.account_id) === String(selectedId),
        );
        setAccountTagsTarget(account?.tags ?? []);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when parent passes new accounts
    }, [googleEnhanceData]);
    useEffect(() => {
        if (fieldList !== undefined && fieldList.length > 0) {
            let mappings = {
                mappings: [],
            };
            [...fieldList].forEach((item) => {
                mappings.mappings.push({
                    event:
                        item.event === "custom"
                            ? item?.customEvent
                            : item?.event,
                    target: {
                        ...(item?.targetId ? { id: item?.targetId } : {}),
                        ...(item.target === "custom"
                            ? {
                                  tag: item?.customTarget,
                                  name: item?.customTarget,
                                  ...(!isWebDestination && item?.customTarget
                                      ? {
                                            id: item?.customTarget,
                                        }
                                      : {}),
                              }
                            : {}),
                        ...(item.target !== "custom"
                            ? {
                                  name: item?.targetName,
                                  ...(parseInt(item?.target)
                                      ? {
                                            id: parseInt(item?.target),
                                        }
                                      : { tag: item?.target }),
                              }
                            : {}),
                        is_custom: item?.target === "custom",
                        is_online: isWebDestination,
                    },
                });
            });
            if (setEnhanceMapping !== undefined) {
                setEnhanceMapping(mappings);
            }
        } else if (setEnhanceMapping !== undefined) {
            setEnhanceMapping({ mappings: [] });
        }
    }, [fieldList, isWebDestination]);
    useEffect(() => {
        if (
            ggadsDestinationId !== undefined &&
            editMappingStatu !== undefined &&
            editMappingStatu === true &&
            eventList?.length > 0
        ) {
            let id = ggadsDestinationId;
            const _getGoogleMapping = async () => {
                setIsLoading(true);
                // Use appropriate API based on destination type
                let response = isWebDestination
                    ? await getGoogleWebMapping(id)
                    : await getGoogleEnhanceMapping(id);
                if (response.status) {
                    let mappings = response?.data?.mappings;
                    const tags = response?.data?.tags;
                    setAccountTagsTarget(tags);
                    let fields = [];
                    mappings.forEach((item) => {
                        const isCustomEvent = !eventList?.find(
                            (eventItem) => eventItem?.api_value === item.event,
                        );
                        fields.push({
                            event: isCustomEvent ? "custom" : item?.event,
                            customEvent: isCustomEvent ? item?.event : "",
                            target: !!item?.target.is_custom
                                ? "custom"
                                : item?.target?.tag !== " " && item?.target?.tag
                                  ? item?.target?.tag ||
                                    item?.target?.id ||
                                    item?.target?.name
                                  : item?.target?.id,
                            targetId: item?.target?.id,
                            targetError: false,
                            customTarget: !!item?.target.is_custom
                                ? item?.target?.tag || item?.target?.name
                                : "",
                            customTargetError: false,
                        });
                    });
                    setFieldList(fields);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);

                    SwalAlertComp({
                        title: language?.delete_swal_modal?.failed ?? "Failed",
                        message: response.message && response.message,
                        icon: "error",
                    });
                }
            };
            if (fieldList.length === 0) {
                _getGoogleMapping();
            }
        }
    }, [eventList, isWebDestination]);

    const eventSelect = useRef(null);

    useEffect(() => {
        /** 
        setTimeout(() => {
            if (eventSelect.current?.inputRef !== null) {
                if (eventSelect.current.getValue()[0].api_value === "") {
                    eventSelect.current.inputRef.placeholder =
                        language?.google_offline?.select_event ??
                        "Select Event";
                }
            }
        }, 100);
        */
        if (!fieldList?.length) {
            setButtonDisabledStatus(true);
            return;
        }

        fieldList.forEach((item) => {
            if (item?.event === "" || item?.target === "") {
                setButtonDisabledStatus(true);
            } else if (item?.event !== "" && item?.target !== "") {
                setButtonDisabledStatus(false);
            }
        });
    }, [fieldList]);

    return (
        <div className={`mt-3 tour-google-fields`}>
            {isLoading && (
                <Box width={"100%"} display="flex" justifyContent={"center"}>
                    <ClipLoader />
                </Box>
            )}
            {!isLoading && (
                <Fragment>
                    {adAccountIdList && adAccountIdList.length > 0 && (
                        <FormControl fullWidth>
                            <Autocomplete
                                value={
                                    adAccountIdList?.find(
                                        (option) =>
                                            option?.api_value ===
                                            selectedAdAccountId,
                                    ) || null
                                }
                                options={adAccountIdList}
                                getOptionLabel={(option) =>
                                    `${option?.display_value}${
                                        option?.display_value ? " - " : ""
                                    }${option?.api_value}`
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option?.api_value === value?.api_value
                                }
                                onChange={(e, value) =>
                                    handleAdAccountIdChange({
                                        target: {
                                            value: value?.api_value,
                                        },
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Ad Account ID"
                                    />
                                )}
                                className="mb-3"
                                fullWidth
                            />
                        </FormControl>
                    )}
                    {fieldList &&
                        fieldList.length > 0 &&
                        fieldList.map((item, index) => (
                            <div
                                style={{
                                    display: "flex",
                                    width: "100%",
                                    gap: "12px",
                                    alignItems: "center",
                                }}
                            >
                                <Row
                                    className="mb-2 text-left"
                                    style={{
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                    }}
                                >
                                    <Col xs={6}>
                                        {item?.event === "custom" ? (
                                            <TextField
                                                label="Custom Event"
                                                value={item.customEvent}
                                                error={item?.customEventError}
                                                required
                                                onChange={(e) => {
                                                    handleEventChange(
                                                        e,
                                                        index,
                                                        true,
                                                    );
                                                }}
                                                fullWidth
                                                className="text-field-ui-workaround"
                                            />
                                        ) : (
                                            <FormControl fullWidth>
                                                <Autocomplete
                                                    label="Event"
                                                    value={
                                                        eventList?.find(
                                                            (event) =>
                                                                event?.api_value ===
                                                                item.event,
                                                        ) ||
                                                        item.event ||
                                                        null
                                                    }
                                                    error={item.eventError}
                                                    options={eventList}
                                                    getOptionLabel={(option) =>
                                                        `${option?.display_value}`
                                                    }
                                                    required
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Event"
                                                        />
                                                    )}
                                                    onChange={(e, value) =>
                                                        handleEventChange(
                                                            {
                                                                target: {
                                                                    value,
                                                                },
                                                            },
                                                            index,
                                                        )
                                                    }
                                                    fullWidth
                                                    renderOption={(
                                                        props,
                                                        option,
                                                        { selected },
                                                    ) => {
                                                        const {
                                                            key,
                                                            ...optionProps
                                                        } = props;
                                                        const optionIndex =
                                                            props[
                                                                "data-option-index"
                                                            ];

                                                        return (
                                                            <Fragment>
                                                                <li
                                                                    key={key}
                                                                    {...optionProps}
                                                                    style={{
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        overflow:
                                                                            "hidden",
                                                                        textOverflow:
                                                                            "ellipsis",
                                                                        maxWidth:
                                                                            "100%", // ensures it doesn't overflow container
                                                                    }}
                                                                    title={
                                                                        option?.display_value
                                                                    } // shows full text on hover
                                                                >
                                                                    {
                                                                        option?.display_value
                                                                    }
                                                                </li>
                                                                {optionIndex ===
                                                                    eventList?.length -
                                                                        1 && (
                                                                    <MenuItem
                                                                        onClick={() =>
                                                                            handleEventChange(
                                                                                {
                                                                                    target: {
                                                                                        value: "custom",
                                                                                    },
                                                                                },
                                                                                index,
                                                                            )
                                                                        }
                                                                    >
                                                                        Custom
                                                                        Event
                                                                    </MenuItem>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    }}
                                                    ref={eventSelect}
                                                ></Autocomplete>
                                            </FormControl>
                                        )}
                                    </Col>
                                    <Col xs={6}>
                                        <FormControl
                                            className="position-relative"
                                            fullWidth
                                        >
                                            {item?.target === "custom" ? (
                                                <TextField
                                                    label="Custom Target"
                                                    value={item.customTarget}
                                                    error={
                                                        item.customTargetError
                                                    }
                                                    required
                                                    placeholder={
                                                        isWebDestination
                                                            ? "AW-1234567890/AbC123_xyz"
                                                            : "Enter target ID"
                                                    }
                                                    onChange={(e) =>
                                                        handleTargetChange(
                                                            e,
                                                            index,
                                                            true,
                                                        )
                                                    }
                                                    fullWidth
                                                    type={
                                                        isWebDestination
                                                            ? "text"
                                                            : "number"
                                                    }
                                                    className="text-field-ui-workaround"
                                                />
                                            ) : (
                                                <FormControl fullWidth>
                                                    <Autocomplete
                                                        label="Target"
                                                        value={
                                                            filteredAccountTagsTarget?.find(
                                                                (account) =>
                                                                    account?.tag ===
                                                                        item.target ||
                                                                    account?.id ===
                                                                        parseInt(
                                                                            item?.target,
                                                                        ),
                                                            ) || null
                                                        }
                                                        error={item.targetError}
                                                        options={
                                                            filteredAccountTagsTarget
                                                        }
                                                        getOptionLabel={(
                                                            option,
                                                        ) =>
                                                            `${
                                                                option?.name
                                                            } - ${
                                                                option?.tag ||
                                                                option?.id
                                                            }`
                                                        }
                                                        slotProps={{
                                                            popper: {
                                                                modifiers: [
                                                                    {
                                                                        placement:
                                                                            "bottom-start",
                                                                        name: "setWidth",
                                                                        enabled: true,
                                                                        phase: "beforeWrite",
                                                                        modifiers:
                                                                            [
                                                                                {
                                                                                    name: "offset",
                                                                                    options:
                                                                                        {
                                                                                            offset: [
                                                                                                0,
                                                                                                10,
                                                                                            ],
                                                                                        },
                                                                                },
                                                                            ],
                                                                        requires:
                                                                            [
                                                                                "computeStyles",
                                                                            ],
                                                                        fn({
                                                                            state,
                                                                        }) {
                                                                            state.styles.popper.minWidth =
                                                                                state.styles.popper.width;
                                                                            state.styles.popper.width =
                                                                                "unset";
                                                                        },
                                                                    },
                                                                ],
                                                            },
                                                        }}
                                                        required
                                                        renderInput={(
                                                            params,
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label="Target"
                                                            />
                                                        )}
                                                        onChange={(e, value) =>
                                                            handleTargetChange(
                                                                {
                                                                    target: {
                                                                        value,
                                                                    },
                                                                },
                                                                index,
                                                            )
                                                        }
                                                        fullWidth
                                                        renderOption={(
                                                            props,
                                                            option,
                                                            { selected },
                                                        ) => {
                                                            const {
                                                                key,
                                                                ...optionProps
                                                            } = props;
                                                            const optionIndex =
                                                                props[
                                                                    "data-option-index"
                                                                ];
                                                            return (
                                                                <Fragment>
                                                                    <li
                                                                        key={
                                                                            key
                                                                        }
                                                                        {...optionProps}
                                                                        style={{
                                                                            whiteSpace:
                                                                                "nowrap",
                                                                            overflow:
                                                                                "hidden",
                                                                            textOverflow:
                                                                                "ellipsis",
                                                                            maxWidth:
                                                                                "100%", // Ensures it doesn't exceed width
                                                                        }}
                                                                        title={`${
                                                                            option?.name
                                                                        } - ${
                                                                            option?.tag ||
                                                                            option?.id
                                                                        }`} // Show full text on hover
                                                                    >
                                                                        {
                                                                            option?.name
                                                                        }{" "}
                                                                        -{" "}
                                                                        {option?.tag ||
                                                                            option?.id}
                                                                    </li>
                                                                    {optionIndex ===
                                                                        filteredAccountTagsTarget?.length -
                                                                            1 && (
                                                                        <MenuItem
                                                                            onClick={() =>
                                                                                handleTargetChange(
                                                                                    {
                                                                                        target: {
                                                                                            value: "custom",
                                                                                        },
                                                                                    },
                                                                                    index,
                                                                                )
                                                                            }
                                                                        >
                                                                            Custom
                                                                            Target
                                                                        </MenuItem>
                                                                    )}
                                                                </Fragment>
                                                            );
                                                        }}
                                                    />
                                                </FormControl>
                                            )}
                                        </FormControl>
                                    </Col>
                                </Row>
                                <Tooltip
                                    title={
                                        fieldList?.length === 1 &&
                                        "At least 1 mapping required!"
                                    }
                                >
                                    <Box>
                                        <StandartButton
                                            iconButton
                                            variant={ButtonVariant.DANGER}
                                            disabled={fieldList?.length === 1}
                                            onClick={(e) => {
                                                removeField(index);
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </StandartButton>
                                    </Box>
                                </Tooltip>
                            </div>
                        ))}
                    <div className="mt-3">
                        <StandartButton
                            variant={ButtonVariant.BLACK_OUTLINED}
                            onClick={addField}
                        >
                            <AddIcon />
                            {fieldList?.length === 0
                                ? (language?.buttons?.add_a_row ?? "Add a Row")
                                : (language?.buttons?.add_new_row ??
                                  "Add New Row")}
                        </StandartButton>
                    </div>
                </Fragment>
            )}
        </div>
    );
};

export default GoogleOfflinePixels;
